// Layering exception (accepted): webmodule-common referencing a bundle's generated CurrencyCode. It is a
// stable ISO-4217 leaf enum used here as a runtime value (CurrencyCode.USD, Object.keys). Owning a canonical
// copy in webmodule-common would make it a *nominally* different enum from the generated one, forcing casts
// at every dealer/supplier call site that passes a currency into these shared formatters. Not worth it.
import { CurrencyCode } from '../../scripts/api/dealer-api-interface-quote';
import { html } from 'lit';

function getCurrencyFormatter(locale: string = 'en-US', currency: CurrencyCode = CurrencyCode.USD) {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCodeToString(currency),
    currencySign: 'accounting',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function getCurrencyFormatter4dp(locale: string = 'en-US', currency: CurrencyCode = CurrencyCode.USD) {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCodeToString(currency),
    currencySign: 'accounting',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4
  });
}

function getCurrencyFormatterCompact(locale: string = 'en-US', currency: CurrencyCode = CurrencyCode.USD) {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCodeToString(currency),
    currencySign: 'accounting',
    notation: 'compact',
    maximumFractionDigits: 1
  });
}

const parts = getCurrencyFormatter().formatToParts(99999999999);
const partDecimal = parts.find(x => x.type === 'decimal')?.value;

const regPattern = new RegExp(`[^-0-9${partDecimal}]*`, 'mg');
export function strToMoney(str: string, digits = 2): number {
  const value = str.replaceAll(regPattern, '');
  const val = parseFloat(value);
  if (!isNaN(val)) return money(val, digits);
  else return val;
}

export function money(value: number, digits = 2) {
  return parseFloat(value.toFixed(digits));
}

export function moneyToStr(
  value: number | string,
  currency: CurrencyCode = CurrencyCode.USD,
  locale: string = 'en-US'
): string {
  if (typeof value === 'string') {
    value = strToMoney(value);
  }
  return getCurrencyFormatter(locale, currency).format(value);
}

export function money4dpToStr(
  value: number,
  currency: CurrencyCode = CurrencyCode.USD,
  locale: string = 'en-US'
): string {
  return getCurrencyFormatter4dp(locale, currency).format(value);
}

export function moneyToCompactStr(
  value: number,
  currency: CurrencyCode = CurrencyCode.USD,
  locale: string = 'en-US'
): string {
  return getCurrencyFormatterCompact(locale, currency).format(value);
}

export function moneyToHtml(value: number, currency: CurrencyCode = CurrencyCode.USD, locale: string = 'en-US') {
  return `<span class="${value < 0 ? 'text-danger' : ''}">${moneyToStr(value, currency, locale)}</span>`;
}
export function money4dpToHtml(value: number) {
  return `<span class="${value < 0 ? 'text-danger' : ''}">${money4dpToStr(value)}</span>`;
}
export function moneyToTemplateResult(
  value: number | string,
  currency: CurrencyCode = CurrencyCode.USD,
  locale: string = 'en-US'
) {
  const numValue = typeof value === 'string' ? strToMoney(value) : value;
  return html`<span class="tab-value${numValue < 0 ? ' text-danger' : ''}"
    >${moneyToStr(value, currency, locale)}</span
  >`;
}
export function money4dpToTemplateResult(
  value: number,
  currency: CurrencyCode = CurrencyCode.USD,
  locale: string = 'en-US'
) {
  return html`<span class="${value < 0 ? 'text-danger' : ''}">${money4dpToStr(value, currency, locale)}</span>`;
}

export function number2dpToHtml(value: number) {
  return html`<span>${value.toFixed(2)}</span>`;
}

export function number4dpToHtml(value: number) {
  return html`<span>${value.toFixed(4)}</span>`;
}

/**
 * Converts a CurrencyCode enum value to the ISO currency code string (e.g., CurrencyCode.USD => 'USD').
 */
export function currencyCodeToString(code: CurrencyCode): string {
  const key = Object.keys(CurrencyCode).find(k => (CurrencyCode as any)[k] === code);
  return key ?? 'USD';
}

/**
 * Converts a string into a CurrencyCode enum value.
 * If the provided string matches a key in CurrencyCode (case-sensitive), returns that enum value; otherwise defaults to CurrencyCode.USD.
 */
export function stringToCurrencyCode(value: string | null | undefined): CurrencyCode {
  if (!value) return CurrencyCode.USD;
  const keyMatch = Object.keys(CurrencyCode).find(k => k === value);
  if (keyMatch) {
    return (CurrencyCode as any)[keyMatch] as CurrencyCode;
  }
  return CurrencyCode.USD;
}

class GlobalCurrencyState {
  private static instance: GlobalCurrencyState;
  private _currency?: CurrencyCode;

  private constructor() {}

  static getInstance(): GlobalCurrencyState {
    if (!GlobalCurrencyState.instance) {
      GlobalCurrencyState.instance = new GlobalCurrencyState();
    }
    return GlobalCurrencyState.instance;
  }

  set currency(value: CurrencyCode | undefined) {
    this._currency = value;
  }

  get currency(): CurrencyCode | undefined {
    return this._currency;
  }
}

export const globalCurrencyState = GlobalCurrencyState.getInstance();
